/* istanbul ignore file */
/**
 * Created by rathompson on 5/2/22
 * File: AssociationTable
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useTableInstance } from '@panwds/react-table'
import {
  ASSOCIATION_TABLE_TITLE,
  BUTTON_REMOVE_APP_ASSC,
  BUTTON_REMOVE_ASSOCIATIONS, BUTTON_REMOVE_TSG_ASSC,
  TITLE_DISASSOCIATE_APPS, TOOLTIP_REMOVE_APP_ASSC, TOOLTIP_REMOVE_TSG_ASSC,
} from '../../constants/LabelConstants'
import TableHeader from '../../../common/components/table/TableHeader'
import { Button, LoadingIcon, Select, Tooltip, TruncateText, useModal } from '@panwds/react-ui'
import { ARRAY, getAppDisplayNameOverride, hasTsgScmBase, showDasDeviceAssociations, hasTsgScmPremium } from '../../../common/utils/Utils'
import { setSidecarOptions } from '../../../common/reducers/actions/ViewReducer.actions'
import DeviceTable from './DeviceTable'
import styled from 'styled-components'
import { DEVICE_STATUS_BLACKLIST, DEVICE_STATUSES } from '../../constants/Constants'
import SubscriptionService from '../../../subscription/services/SubscriptionService'
import RemoveTSGModal from '../modal/RemoveTSGModal'
import { getVarValue } from '../../../common/utils/Sparky'
import { FF_COMMON_SERVICES_CUSTOM_ROLES, FF_ENABLE_SCM_APP } from '../../../common/utils/FeatureFlags'
import { getTsgAccess } from '@sparky/framework'
import { DEVICES_PERMISSION_SET, HIDE_VIEW_ASSOCIATION_APPS_DAS, SPARKY_ACCESS_READ } from '../../../common/constants/GlobalConstants'
import GlobalContext from '../../../common/context/GlobalContext'
import useReadOnly from '../../../common/hooks/useReadOnly'
import { intersection } from 'lodash'
import scmBase from '../../../activation/manifests/scmBase'

const CONTAINER = styled.div`
  position: absolute;
  inset: 0 0 0 0;
  height: 100%;
  display: flex;
  flex-direction: column;

  .tw-border-solid.tw-border-gray-200.tw-border-0.tw-flex.tw-relative.tw-bg-white.tw-border.tw-overflow-auto {
    flex: 1 1;
  }
  button[id^=table] {
    color: #333333;
  }

  button[aria-label="Previous"] {
    color: #333333;
  }
  button[aria-label="Next"] {
    color: #333333;
  }

  div[role="tooltip"] {
    z-index: 1055;
  }

  .tw-flex.tw-bg-white {
    flex: 1 1;
  }

  input[type=checkbox] {
    min-height: 1rem;
    min-width: 1rem;
    font-family: inherit !important;
    position: initial !important;
    -moz-appearance: auto !important;
    -webkit-appearance: checkbox !important;
    -o-appearance: auto !important;

    &[disabled] {
      background-image: none;
      border-width: 1px;
      border-style: solid;
      border-radius: 0.063rem;
      --tw-border-opacity: 1;
      border-color: rgba(218,219,219,var(--tw-border-opacity));
      background-color: transparent;
    }
  }
  input[type=checkbox]::before {
    display: none !important;
  }
`

const TooltipItem = ({ label, body, disabled, onClick: _onClick }) => {
  const className = `tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center ${disabled ? 'tw-text-disabled tw-cursor-not-allowed' : ''}`.trim()

  const onClick = useCallback(() => {
    if (disabled) {
      return
    }
    if (_onClick) {
      _onClick()
    }
  }, [disabled, _onClick])

  return (
    <div {...{ className, onClick }}>
      {disabled ? (
        <Tooltip label={label}>
          <span>{body}</span>
        </Tooltip>
      ) : (
        <span>{body}</span>
      )}
    </div>
  )
}


const RightControls = ({
  onRemoveTenant,
  onDisassociateClick,
  setNewDevices,
  isImplicitDeviceAssociationEnabledForTSG
}) => {
  const { treeReducer } = useContext(GlobalContext)
  const { selectedTreeNode = {} } = treeReducer
  const removeTSGModal = useModal(RemoveTSGModal)
  const readOnly = useReadOnly(selectedTreeNode?.id, DEVICES_PERMISSION_SET)

  const useSelectedRowData = (tableInstance) => {
    const statusBlacklist = DEVICE_STATUS_BLACKLIST
    return Object.keys(tableInstance.state.selectedRowIds)
      .map((index) => tableInstance.data[Number(index)])
      .filter(device => Boolean(device) && (!ARRAY.hasArray(device.associations) || !device?.associations?.every(({ association_status }) => statusBlacklist.includes(association_status))))
  }
  const selected = useSelectedRowData(useTableInstance())


  const hasAssociations = useMemo(() => {
    const devicesWithAssociations = selected.filter(device => ARRAY.hasArray(device?.associations))
    if (!isImplicitDeviceAssociationEnabledForTSG) {
      // if the feature to show view only associations is not enabled - fall back to previous logic
      return devicesWithAssociations.length > 0
    }

    // there are devices with associations - check if they dont have view only association
    const devicesWithRemovableAppAssociations = devicesWithAssociations.filter(device => {
      if (ARRAY.hasArray(device?.associations)) {
        const arrLength = device.associations.length
        const viewAppsLength = device.associations.filter(association => association.isFakeAppAssociation).length
        // there are apps which are view only apps. They can be de-associated
        return (arrLength > viewAppsLength)
      }

      return false
    })

    return devicesWithRemovableAppAssociations.length > 0
  }, [selected, isImplicitDeviceAssociationEnabledForTSG])

  const onRemoveClick = useCallback(() => {
    setNewDevices([])
    const callback = async () => {
      await onRemoveTenant(selected)
    }
    removeTSGModal.openModal({ callback })
  }, [setNewDevices, onRemoveTenant, removeTSGModal, selected])

  const onRemoveApp = useCallback(() => {
    setNewDevices([])
    onDisassociateClick(selected)
  }, [setNewDevices, onDisassociateClick, selected])

  const items = useMemo(() => [
    {
      value: <TooltipItem
        body={BUTTON_REMOVE_APP_ASSC}
        label={TOOLTIP_REMOVE_APP_ASSC}
        disabled={!hasAssociations}
        onClick={onRemoveApp}
      />
    },
    {
      value: <TooltipItem
        body={BUTTON_REMOVE_TSG_ASSC}
        label={TOOLTIP_REMOVE_TSG_ASSC}
        disabled={hasAssociations}
        onClick={onRemoveClick}
      />
    }
  ], [hasAssociations, onRemoveApp, onRemoveClick])
  return (
    <div className='tw-flex tw-gap-2 tw-items-center'>
      <Select
        addClassName='cmm-modal-select'
        items={items}
        disabled={!ARRAY.hasArray(selected) || readOnly}
        // fitToItemWidth not working
        menuStyle={{ minWidth: '12rem' }}
        button={(
          <Button
            addClassName='tw-justify-between'
            fullWidth
            appearance='secondary'
            isMenu
          >
            {BUTTON_REMOVE_ASSOCIATIONS}
          </Button>
        )}/>
    </div>
  )
}

const AssociationTable = (props) => {

  const {
    selectedTreeNode = {},
    dispatch = () => null,
    devices = [],
    instances = [],
    fetchDevices: _fetchDevices = async () => [],
    createMessage = (appearance, message) => {},
    setError = (message) => {},
    products = [],
    isImplicitDeviceAssociationEnabledForTSG = false
  } = props

  const [loading, setLoading] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [newDevices, setNewDevices] = useState([])
  const tableRef = useRef()

  const shouldRefresh = useMemo(() => {
    return devices?.some(
      device => ARRAY.hasArray(device?.associations) && device?.associations?.some(
        ({ association_status }) => [DEVICE_STATUSES.ASSOCIATING, DEVICE_STATUSES.DISSOCIATING].includes(association_status)))
  }, [devices])

  useEffect(() => {
    let id = null
    async function refresh() {
      if (!shouldRefresh) {
        clearTimeout(id)
        id = null
        return
      }
      setLoading(true)
      await _fetchDevices()
      setLoading(false)
      if (id != null && shouldRefresh) {
        id = setTimeout(refresh, 3000)
      }
    }
    id = setTimeout(refresh, 3000)
    return () => {
      clearTimeout(id)
      id = null
    }
  }, [_fetchDevices, shouldRefresh])

  const title = useMemo(() => {
    return (
      <div className='tw-flex tw-gap-2 tw-overflow-hidden'>
        {ASSOCIATION_TABLE_TITLE}<TruncateText addClassName="tw-block">{selectedTreeNode?.display_name}</TruncateText>
      </div>
    )
  }, [selectedTreeNode])

  /* These memoized methods are for implementing custom logic for SCM use cases
      1) Show 'prisma_access' association only if the TSG has SCM instance & association in DAS
      2) Override display_name based on the SCM tier
  */
  const isTsgWithScmBase = useMemo(() => {
    return getVarValue(FF_ENABLE_SCM_APP) && hasTsgScmBase({ instances })
  }, [instances])

  const isTsgWithScmPremium = useMemo(() => {
    return getVarValue(FF_ENABLE_SCM_APP) && hasTsgScmPremium({ instances })
  }, [instances])


  const data = useMemo(() => {
    const apps = products
    const appMap = new Map(apps?.map(app => [app.app_id, app]))
    const isTsgWithScmApp = isTsgWithScmBase || isTsgWithScmPremium
    // find which apps we need to include in association
    const viewApps = products.filter(product => product.device_view_association && !product.device_create_association)
    return devices?.map(device => {
      const excludedAppsFromImplicitView = new Set(HIDE_VIEW_ASSOCIATION_APPS_DAS) //Exclude apps like 'prisma_access' from implicit view
      const deviceAssociations = device?.associations?.filter(association => {
        return showDasDeviceAssociations(association, isTsgWithScmApp) //Filter out 'prisma_access' association if the TSG doesn't have SCM
      })
      const associations = deviceAssociations?.map(association => {
        const app = appMap.get(association.app_id)
        excludedAppsFromImplicitView.add(association.app_id)
        if (app && (app?.app_id_display_override)) {
          association.app_id_display_override = app?.app_id_display_override
        }

        //app_display_name_override will override the default display name
        association.app_display_name_override = getVarValue(FF_ENABLE_SCM_APP) ? getAppDisplayNameOverride(app, isTsgWithScmBase, isTsgWithScmPremium) : null

        // add a flag called isFakeAppAssociation to indicate that this is not implicit association
        association.isFakeAppAssociation = association.isFakeAppAssociation || viewApps.includes(app)
        return association
      })
      if (viewApps.length && isImplicitDeviceAssociationEnabledForTSG) {
        // Exclude implicit apps which have association in DAS
        viewApps?.filter(app => !excludedAppsFromImplicitView.has(app.app_id))?.forEach((_viewApp) => {
          const _viewAppAssociation = Object.assign({}, _viewApp)
          if (_viewAppAssociation) {
            // SLS to device is mapping on DAS side is implicit and its fire and forget model with cdl notification, hence always mark it as completed
            _viewAppAssociation.association_status = DEVICE_STATUSES.COMPLETED
            // add a flag called isFakeAppAssociation to indicate that we added implicit association
            _viewAppAssociation.isFakeAppAssociation = true
            associations.push(_viewAppAssociation)
          }
        })
      }
      device.associations = associations
      return device
    })
  }, [devices, isTsgWithScmBase, isTsgWithScmPremium, products, isImplicitDeviceAssociationEnabledForTSG])

  const fetchDevices = useCallback(async () => {
    try {
      setNewDevices([])
      await _fetchDevices()
    }
    catch (error) {
      setError(error.message)
    }
  }, [_fetchDevices, setError])

  const onDisassociateClick = useCallback((selected) => {
    setNewDevices([])
    dispatch(setSidecarOptions(true, TITLE_DISASSOCIATE_APPS, 'apps', {
      tenant: selectedTreeNode,
      action: 'remove',
      devices: selected,
      instances,
      callback: ({ devices, apps }) => {
        createMessage('success', `App dissociation has been initiated for ${devices.length} devices.`)
        tableRef.current?.toggleAllRowsSelected(false)
      },
      fetchDevices
    }))
  }, [createMessage, dispatch, fetchDevices, instances, selectedTreeNode])

  const onRemoveTenant = useCallback(async (selected) => {
    setNewDevices([])
    try {
      const tsg_id = selectedTreeNode?.id
      await SubscriptionService.devices.dissociateDevices({
        tsg_id,
        devices: selected.map(device => ({ serial_number: device?.serial_number }))
      })
      await fetchDevices()
      setIsLoading(false)
      createMessage('success', `${selected?.length} devices have been disassociated from ${selectedTreeNode?.display_name}`)
    }
    catch (error) {
      setError(error)
    }
    tableRef.current?.toggleAllRowsSelected(false)
  }, [fetchDevices, selectedTreeNode?.id, selectedTreeNode?.display_name, createMessage, setError])

  const shouldHighlightRow = useCallback(({ row }) => {
    return newDevices.length && newDevices?.find(device => device.serial_number === row?.original?.serial_number)
  }, [newDevices])

  return (
    <CONTAINER className='tw-p-3' id='cloud-mgm-asc-table-container'>
      {isLoading && (
        <div className="tw-flex tw-absolute tw-inset-0 tw-justify-center tw-items-center tw-z-50 tw-bg-black tw-opacity-60">
          <LoadingIcon size='4xl' />
        </div>
      )}
      <DeviceTable
        data={data}
        products={products}
        loading={loading}
        tableRef={tableRef}
        shouldHighlightRow={shouldHighlightRow}
        searchCallback={() => setNewDevices([])}
        header={
          <TableHeader title={title}>
            <RightControls
              {...{
                onRemoveTenant,
                onDisassociateClick,
                setNewDevices,
                isImplicitDeviceAssociationEnabledForTSG
              }}
            />
          </TableHeader>
        }/>
    </CONTAINER>
  )
}

AssociationTable.propTypes = {}
export default AssociationTable
